import { useMutation } from "@apollo/client/react";
import { create, show, useModal } from "@ebay/nice-modal-react";

import FlashContext from "components/FlashMessages/FlashContext";
import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { AuthorizationScheme } from "types/generated";

import { UPDATE_ACCOUNT_AUTH_SCHEME } from "./gql";
import { humanizeAuthorizationSchemeName } from "./helpers";

type ChangeManagementStrategyConfirmationModalProps = {
  scheme: AuthorizationScheme;
  name: string;
  keysCount: number;
  onSwitched: () => void;
};

const ChangeManagementStrategyConfirmationModal = create(
  function ChangeManagementStrategyConfirmationModal({
    scheme,
    name,
    keysCount,
    onSwitched,
  }: ChangeManagementStrategyConfirmationModalProps) {
    const modal = useModal();
    const { personalApiKeys } = useTypedFlags();
    const revokesPersonalKeys = personalApiKeys && scheme === AuthorizationScheme.LoginPolicies;
    const { onError, reportSuccess } = useTypedContext(FlashContext);

    const [updateAccountAuthScheme, { loading }] = useMutation(UPDATE_ACCOUNT_AUTH_SCHEME, {
      refetchQueries: ["GetAuthorizationScheme"],
    });

    const handleOnConfirm = () => {
      updateAccountAuthScheme({ variables: { schema: scheme } })
        .then(({ data }) => {
          if (!data) return;

          reportSuccess({
            message: `Management strategy was changed to ${humanizeAuthorizationSchemeName(
              data.accountUpdateAuthorizationScheme
            )}`,
          });
          onSwitched();
          modal.resolve();
          modal.hide();
        })
        .catch(onError);
    };

    return (
      <ConfirmationModal
        title="Switch management strategy"
        confirmationButtonLabel="Switch strategy"
        confirmationButtonVariant="primary"
        onConfirm={handleOnConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <ConfirmationModalMessage>
          {revokesPersonalKeys ? (
            <>
              After switching to {name}, all active sessions (except the current one) will be
              invalidated and{" "}
              {keysCount > 0 ? <strong>{keysCount} personal API keys</strong> : "personal API keys"}{" "}
              will be revoked. This can&apos;t be undone.
            </>
          ) : (
            <>
              After switching to {name}, all active sessions (except the current one) will be
              invalidated.
            </>
          )}
          <br />
          <br />
          Are you sure you want to switch to <strong>{name}</strong>?
        </ConfirmationModalMessage>
      </ConfirmationModal>
    );
  }
);

export const showChangeManagementStrategyConfirmationModal = (
  props: ChangeManagementStrategyConfirmationModalProps
) => show(ChangeManagementStrategyConfirmationModal, props);
